import React, { memo, useState } from "react";
import { RCCGrid, RCCHookToast, RCCIcon } from "react-common-components";
import List from "src/components/List";
import { useTranslation } from "src/context/Translations/context";
import InfiniteScrollWithoutFetch from "src/components/InfiniteScrollWithoutFetch";
import formatNumber from "src/utils/formatNumber";
import { v4 } from "uuid";
import formatDate from "src/utils/formatDate";
import Title from "src/components/Title";
import { standardErrorMessages } from "src/utils/errors";
import stringToDate from "src/utils/stringToDate";
import ExportListSpace from "src/components/List/exportListSpace";
import * as ListStyles from "./styles";
import { IListIncomeValue } from "./interface";
import { columnLastYear, columnPlanned, columnRealized, formatValue, headerColumnLastYear, headerColumnPlanned, headerColumnRealized } from "./columns";
import executeQueryIncomeChildren from "../../executeQueryIncomeChildren";

const ListTable: React.FC<IListIncomeValue> = ({
	data,
	memorizedUrlParams,
	baseVerticalValues,
	valuesOfQuantity,
	childLevel = 0,
	showHeader = true,
	showForecast = true,
	showOnlyAccumulated = true,
	showColumnLastYear = true,
	showColumnPlanned = true,
	showColumnRealized = true,
	showVariationPercent = true,
	showVariationValue = true,
	showHorizontalAnalysis = true,
	showQuantity = true,
	showVerticalAnalysis = true,
	title,
	subsidiary,
	openChartIndicators,
	setLoading,
	// mouseOut,
	// mouseOver,
	dtForecast,
	coinPlannedInitials,
	coinRealizedInitials,
	onMouseDown
}) => {
	const { translate, currentLanguage } = useTranslation();
	const { addToast } = RCCHookToast();
	const [listData, setListData] = useState(data);

	const loadChildrens = async (origin: number, lineIdFather: number, lineId?: number, passBaseVerticalValues?: boolean, collapse?: boolean, passValuesOfQuantity?: boolean) => {
		try{
			setLoading(true);
			const listIncomes = collapse ? [] : await executeQueryIncomeChildren(origin, memorizedUrlParams, lineIdFather, lineId, passBaseVerticalValues ? baseVerticalValues : undefined, passValuesOfQuantity ? valuesOfQuantity : undefined);
			const id = lineId || lineIdFather;
			if(listIncomes.length === 0 && !collapse){
				setLoading(false);
				addToast({
					color: "yellow",
					description: translate("Nenhum valor para hierarquia (drill down) encontrado"),
					title: translate("Atenção")
				});
				return;
			}
			setListData((prevState) => {
				const updateState = [...prevState];
				const index = updateState.findIndex((x) => x.id === id);
				updateState[index].children = listIncomes;
				return updateState;
			});
			setLoading(false);
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao carregar os dados"), addToast);
		}
	};

	const colTitle = (row: any) => (
		<ListStyles.DivAlignCenterWithOverflow fixed>
			<ListStyles.StyledDivIconIndicatorChart>
				{row.indicatorId !== null && (
					<RCCIcon 
						name="chartBar"
						color="var(--default-color-font)"
						onClick={() => {
							if(!openChartIndicators)
								return;
							openChartIndicators(row.indicatorId, row.indicatorDescription);
						}}
					/>
				)}
			</ListStyles.StyledDivIconIndicatorChart>
			{childLevel > 0 && [...Array(childLevel)].map(() => (
				<ExportListSpace key={v4()} />
			))}
			<ListStyles.StyledDivIconIndicatorChart>
				{row.hasChildren && (
					<RCCIcon 
						name={row.children && row.children.length > 0 ? "arrowDown" : "arrowRight"} 
						size={24} 
						style={{ cursor: "pointer" }} 
						onClick={() => loadChildrens(row.origin, 
							row.lineIdFather ? row.lineIdFather : row.id, 
							row.lineIdFather ? row.id : undefined, 
							row.values[0].verticalPredicted !== null || 
							row.values[0].verticalRealized !== null ||
 							row.values[0].verticalLastYear !== null ||
 							row.values[0].verticalForecast  !== null,
							row.children && row.children.length > 0 && true, showQuantity)}
					/>
				)}
			</ListStyles.StyledDivIconIndicatorChart>
			<ListStyles.OverflowSpan title={row.description} bold={row.highlightLine}> 
				{row.description} 
			</ListStyles.OverflowSpan>
		</ListStyles.DivAlignCenterWithOverflow>
	);

	const customHeaderColTitle = () => (<ListStyles.DivColumn alignStart textOverflowEllipsis fixed isHeader>
		<ListStyles.OverflowSpan bold>{title || translate("Estrutura")}</ListStyles.OverflowSpan>
		<ListStyles.OverflowSpan bold purple style={{ fontSize: "0.8rem", height: "32px", width: "100%" }}>
			{translate("Unidade/Filial")}: {subsidiary || translate("Total")}
		</ListStyles.OverflowSpan>
	</ListStyles.DivColumn>);

	const colValues = (row: any) => (
		<ListStyles.DivValues onMouseDown={onMouseDown} >
			<RCCGrid 
				container
				item
				styleInline="flex-wrap: unset !important;"
				className="query-budget-panel"
			>
				{!showOnlyAccumulated && (
					<InfiniteScrollWithoutFetch 
						listElements={row.values}
						mapper={(value: any) => (
							<ListStyles.DivFlex
								key={v4()}
								// onMouseOver={() => mouseOver && mouseOver(value, row.description, row.format === "Q", row.origin)}
								// onMouseOut={() => mouseOut && mouseOut()}
							>
								{showColumnPlanned && !showForecast && columnPlanned(row, value, showVerticalAnalysis, showHorizontalAnalysis, showQuantity, currentLanguage)}
								{showColumnRealized && columnRealized(row, value, showVerticalAnalysis, showHorizontalAnalysis, showQuantity, showForecast, currentLanguage)}
								{showColumnLastYear && columnLastYear(row, value, showVerticalAnalysis, showHorizontalAnalysis, showQuantity, currentLanguage)}
								{showVariationValue && (
									<ListStyles.TD className="value-variation-value">
										<ListStyles.OverflowSpan  
											red={row.format === "Q" ? value.quantityVariationValue < 0 : value.variationValue < 0}
											green={row.format === "Q" ? value.quantityVariationValue > 0 : value.variationValue > 0}
											bold={row.highlightLine}
										>
											{formatNumber(row.format === "Q" ? value.quantityVariationValue : value.variationValue, currentLanguage, 0)}
										</ListStyles.OverflowSpan>
									</ListStyles.TD>
								)}
								{showVariationPercent && (
									<ListStyles.TD className="value-variation-percent">
										<ListStyles.OverflowSpan 
											red={row.format === "Q" ? value.quantityVariationPercent < 0 : value.variationPercent < 0}
											green={row.format === "Q" ? value.quantityVariationPercent > 0 : value.variationPercent > 0}
											bold={row.highlightLine}
										>
											{formatNumber(row.format === "Q" ? value.quantityVariationPercent : value.variationPercent, currentLanguage, 2)}%
										</ListStyles.OverflowSpan>
									</ListStyles.TD>
								)}
							</ListStyles.DivFlex>
						)}
						defaultNumberOfElements={24}
						startIndex={0}
					/>
				)}
				<ListStyles.DivFlex
					// onMouseOver={() => mouseOver && mouseOver(row.accumulatedValues, row.description, row.format === "Q", row.origin)}
					// onMouseOut={() => mouseOut && mouseOut()}
				>
					{showColumnPlanned && !showForecast && columnPlanned(row, row.accumulatedValues, showVerticalAnalysis, showHorizontalAnalysis, showQuantity, currentLanguage, true)}
					{showColumnRealized && columnRealized(row, row.accumulatedValues, showVerticalAnalysis, showHorizontalAnalysis, showQuantity, showForecast, currentLanguage, true)}
					{showColumnLastYear && columnLastYear(row, row.accumulatedValues, showVerticalAnalysis, showHorizontalAnalysis, showQuantity, currentLanguage, true)}
					{showVariationValue && (
						<ListStyles.TD className="value-variation-value">
							<ListStyles.OverflowSpan 
								red={row.format === "Q" ? row.accumulatedValues.quantityVariationValue < 0 : row.accumulatedValues.variationValue < 0}
								green={row.format === "Q" ? row.accumulatedValues.quantityVariationValue > 0 : row.accumulatedValues.variationValue > 0}
								bold
							>
								{formatValue(row.format === "Q" ? row.accumulatedValues.quantityVariationValue : row.accumulatedValues.variationValue, currentLanguage, 0)}
							</ListStyles.OverflowSpan>
						</ListStyles.TD>
					)}
					{showVariationPercent && (
						<ListStyles.TD className="value-variation-percent">
							<ListStyles.OverflowSpan 
								red={row.format === "Q" ? row.accumulatedValues.quantityVariationPercent < 0 : row.accumulatedValues.variationPercent < 0}
								green={row.format === "Q" ? row.accumulatedValues.quantityVariationPercent > 0 :  row.accumulatedValues.variationPercent > 0}
								bold
							>
								{formatValue(row.format === "Q" ? row.accumulatedValues.quantityVariationPercent : row.accumulatedValues.variationPercent, currentLanguage, 2, true)}
							</ListStyles.OverflowSpan>
						</ListStyles.TD>
					)}
				</ListStyles.DivFlex>
			</RCCGrid>
		</ListStyles.DivValues>
	);

	const customHeaderColValues = (rows: any) => {
		const firstRow = rows[0];
		const forecastDate = stringToDate(dtForecast);
		return(
			<ListStyles.DivValues>
				<RCCGrid 
					container
					item
					styleInline="flex-wrap: unset !important;"
					className="query-budget-panel"
				>
					{!showOnlyAccumulated && firstRow.values.map((value: any) => (
						<React.Fragment key={v4()}>
							{showColumnPlanned && !showForecast && headerColumnPlanned(value, showVerticalAnalysis, showHorizontalAnalysis, showQuantity, currentLanguage, translate, coinPlannedInitials)}
							{showColumnRealized && headerColumnRealized(value, showVerticalAnalysis, showHorizontalAnalysis, showQuantity, false, currentLanguage, translate, showForecast && forecastDate && forecastDate < new Date(value.date), coinPlannedInitials, coinRealizedInitials)}
							{showColumnLastYear && headerColumnLastYear(value, showVerticalAnalysis, showHorizontalAnalysis, showQuantity, currentLanguage, translate, coinRealizedInitials)}
							{showVariationValue && (
								<ListStyles.TD>
									<ListStyles.DivColumn>
										<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
											{translate("Variação")} $
										</Title>
										<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
											{formatDate(value.date, currentLanguage, false, "dd/")}
										</Title>
									</ListStyles.DivColumn>
								</ListStyles.TD>
							)}
							{showVariationPercent && (
								<ListStyles.TD>
									<ListStyles.DivColumn>
										<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
											{translate("Variação")} %
										</Title>
										<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
											{formatDate(value.date, currentLanguage, false, "dd/")}
										</Title>
									</ListStyles.DivColumn>
								</ListStyles.TD>
							)}
						</React.Fragment>
					))}
					{showColumnPlanned && !showForecast && headerColumnPlanned(undefined, showVerticalAnalysis, showHorizontalAnalysis, showQuantity, currentLanguage, translate, coinPlannedInitials)}
					{showColumnRealized && headerColumnRealized(undefined, showVerticalAnalysis, showHorizontalAnalysis, showQuantity, showForecast, currentLanguage, translate, false, coinPlannedInitials, coinRealizedInitials)}
					{showColumnLastYear && headerColumnLastYear(undefined, showVerticalAnalysis, showHorizontalAnalysis, showQuantity, currentLanguage, translate, coinRealizedInitials)}
					{showVariationValue && (
						<ListStyles.TD >
							<ListStyles.DivColumn>
								<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
									{translate("Variação")} $
								</Title>
								<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
									{translate("Acumulado")}
								</Title>
							</ListStyles.DivColumn>
						</ListStyles.TD>
					)}
					{showVariationPercent && (
						<ListStyles.TD >
							<ListStyles.DivColumn>
								<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
									{translate("Variação")} %
								</Title>
								<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
									{translate("Acumulado")}
								</Title>
							</ListStyles.DivColumn>
						</ListStyles.TD>
					)}
				</RCCGrid>
			</ListStyles.DivValues>
		);};

	const listChild = (dt: any) => (
		<ListTable 
			data={dt.children}
			setLoading={setLoading}
			childLevel={childLevel + 1}
			memorizedUrlParams={memorizedUrlParams}
			baseVerticalValues={baseVerticalValues}
			valuesOfQuantity={valuesOfQuantity}
			showHeader={false}
			showForecast={showForecast}
			showHorizontalAnalysis={showHorizontalAnalysis}
			showQuantity={showQuantity}
			showVerticalAnalysis={showVerticalAnalysis}
			showOnlyAccumulated={showOnlyAccumulated}
			showColumnLastYear={showColumnLastYear}
			showColumnPlanned={showColumnPlanned}
			showColumnRealized={showColumnRealized}
			showVariationPercent={showVariationPercent}
			showVariationValue={showVariationValue}
			openChartIndicators={openChartIndicators}
			// mouseOut={mouseOut}
			// mouseOver={mouseOver}
			dtForecast={dtForecast}
			coinPlannedInitials={coinPlannedInitials}
			coinRealizedInitials={coinRealizedInitials}
			onMouseDown={onMouseDown}
		/>
	);
		
	return(
		<List 
			data={listData}
			columns={[{
				idColumn: "title",
				nameColumn: translate("Indicador"),
				justify: "left",
				cellRenderer: colTitle,
				customHeader: customHeaderColTitle,
				styleInline: "width: 17.5rem !important; min-height: 24px; z-index: 10; background: var(--li-header-color); @media(max-width: 600px){ width: 13rem !important; } @media(max-width: 400px){ width: 11rem !important; }"
			},
			{
				idColumn: "values",
				nameColumn: translate("Valores"),
				cellRenderer: colValues,
				customHeader: customHeaderColValues,
				styleInline: "flex: 1;"
			}]}
			childrenOfLine={{
				cellRenderer: listChild,
				conditionToRender: (dt: any) => dt.children && dt.children.length > 0
			}}
			showHeader={showHeader}
			headerHiddenXs={false}
			layout="table"
			defaultNumberOfElements={200}
			manuallyToggleShowMore
			styleInline={ListStyles.ListStyles(childLevel + 1)}
		/>
	);};

export default memo(ListTable);
